import { ChoiceGroup, DefaultButton, Dialog, DialogFooter, DialogType, IChoiceGroupOption, Label, PrimaryButton, Spinner, SpinnerSize } from '@fluentui/react';
import * as React from 'react';
import BlockUi from 'react-block-ui';
import 'react-block-ui/style.css';
import * as ReactDOM from 'react-dom';
import { ADDRESS_TYPES } from '../../../config/constants';
import { HealAPI } from '../../../healApi';
import { Address } from '../../../api/address.types';
import { ToastParams } from '../../../redux/message/types';
import { Visit } from '../../../redux/visits/types';
import { StatusOptions } from '../../../types';
import { AddressEditor } from '../../create-visit/components/AddressEditor';

export interface EditAddressDialogProps {
  visit: Visit;
  onClosed?: (success: boolean) => void;
  actions: {
    toast: (params: ToastParams) => void;
  };
}

export interface EditAddressDialogState {
  isSaving: boolean;
  isFetching: boolean;
  visitAddress?: Address;
  addresses: Address[];
}

export class EditAddressDialog {
  public static show(options: EditAddressDialogProps) {
    const dialogNode = document.createElement('div');
    const close = (): void => {
      ReactDOM.unmountComponentAtNode(dialogNode);
      dialogNode.remove();
    };
    ReactDOM.render(<EditAddressDialogInternal {...options} close={close} />, dialogNode);
  }
}

class EditAddressDialogInternal extends React.Component<EditAddressDialogProps & { close: () => void }, EditAddressDialogState> {
  constructor(props: any) {
    super(props);

    this.state = {
      isSaving: false,
      isFetching: false,
      addresses: [],
    };
  }

  public componentDidMount() {
    this.setState({ isFetching: true });
    HealAPI.fetchAccountInfo(this.props.visit.userAccountId).then((response) => {
      if (response.ok && response.data) {
        const visitAddress = response.data.addresses.filter((address) => address.addressId === this.props.visit.addressId)[0];
        this.setState({ addresses: response.data.addresses, visitAddress });
      }
      this.setState({ isFetching: false });
    });
  }

  public render(): JSX.Element {
    const options = ADDRESS_TYPES.map((address) => ({
      key: address.type,
      text: address.label,
      iconProps: { iconName: address.iconName },
    }));

    return (
      <Dialog
        hidden={false}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: 'Edit Visit Address',
        }}
        onDismiss={() => this.close()}
        modalProps={{
          isBlocking: true,
        }}
      >
        <BlockUi
          tag="div"
          blocking={this.state.isSaving || this.state.isFetching}
          loader={<Spinner size={SpinnerSize.large} label={`${this.state.isSaving ? 'Saving' : 'Loading'} address...`} />}
        >
          {/**
           * Important: Setting selectedKey to undefined on initial render (even if just temporarily while fetching) will place
           * the ChoiceGroup into an uncontrolled state and prevent future changes to selectedKey from affecting the component.
           * We use null here instead because the typing for selectedKey has a specific carve-out for it (although the
           * official docs don't actually define this).
           * @see \@fluentui/react IChoiceGroupProps.selectedKey
           */}
          <ChoiceGroup
            label="Location"
            disabled={this.state.isFetching}
            selectedKey={this.state.visitAddress?.addressType ?? null}
            onChange={this.onAddressTypeChange}
            options={options}
          />
          <br />
          {this.state.visitAddress && (
            <>
              <Label>Edit selected address</Label>
              <AddressEditor address={this.state.visitAddress} onChange={this.onAddressChange} />
            </>
          )}
          <DialogFooter>
            <PrimaryButton onClick={this.onEditAddress} disabled={!this.isValid()}>
              Ok
            </PrimaryButton>
            <DefaultButton onClick={() => this.close()}>Cancel</DefaultButton>
          </DialogFooter>
        </BlockUi>
      </Dialog>
    );
  }

  private onAddressTypeChange = (event?: React.FormEvent<HTMLElement>, option?: IChoiceGroupOption) => {
    if (option) {
      const visitAddress = this.state.addresses!.find((a) => a.addressType === option.key) || ({ addressType: option.key } as any as Address);
      this.setState({ visitAddress });
    }
  };

  private onAddressChange = (visitAddress: Address) => {
    this.setState({ visitAddress });
  };

  private onEditAddress = () => {
    const { visitCode } = this.props.visit;
    const address = this.state.visitAddress as Address;

    if (address && this.isValid()) {
      this.setState({ isSaving: true });
      HealAPI.checkAddressCoverage(address.latitude, address.longitude).then((response) => {
        if (response.data.covered) {
          HealAPI.editVisitAddress(visitCode, address).then((responseEditVisitAddress) => {
            if (responseEditVisitAddress.data && responseEditVisitAddress.data.status === StatusOptions.OK) {
              this.props.actions.toast({
                type: 'success',
                title: 'Success',
                content: 'Address Successfully Updated',
              });
              this.setState({ isSaving: false });
              this.close(true);
            } else {
              this.props.actions.toast({
                type: 'error',
                title: 'Error',
                content: responseEditVisitAddress.data?.message || "Can't save visit address, please try again later",
              });
              this.setState({ isSaving: false });
            }
          });
        } else {
          this.props.actions.toast({
            type: 'error',
            title: 'Not Covered',
            content: 'Address is not in a Heal coverage area.',
          });
          this.setState({ isSaving: false });
        }
      });
    }
  };

  private isValid(): boolean {
    const { visitAddress } = this.state;
    return !!visitAddress && !!visitAddress.streetAddress && visitAddress.zipcode.length === 5;
  }

  private close = (success = false) => {
    if (this.props.onClosed) {
      this.props.onClosed(success);
    }
    this.props.close();
  };
}
